import { AppFile, FetchStatus } from '../types/types';
import { createFetchStatusReducer } from '../utils/createFetchStatusReducer';
import {
  FETCH_APP_FILE_FAILURE,
  FETCH_APP_FILE_START,
  FETCH_APP_FILE_SUCCESS,
  FETCH_APP_FILES_FAILURE,
  FETCH_APP_FILES_START,
  FETCH_APP_FILES_SUCCESS,
  REQUEST_FORM,
  REQUEST_FORM_CANCEL,
  REQUEST_FORM_FAILURE,
  REQUEST_FORM_SUCCESS,
  UPLOAD_APP_FILE_SUCCESS,
} from '../modules/files/actionTypes';
import { Action, handleActions } from 'redux-actions';
import {
  FetchAppFilesSuccessPayload,
  FetchAppFileSuccessPayload,
  UploadAppFileSuccessPayload,
} from '../modules/files/actions';
import { combineReducers } from 'redux';
import keyBy from 'lodash/keyBy';
import startsWith from 'lodash/startsWith';
import { getAppFileType } from '../utils/file/getAppFileType';
import { stripFileExtension } from '../utils/stripFileExtension';
import { AppFileType } from 'utils/file/constants';

type FileRecord = Record<string, AppFile>; // key is file name
export interface FilesState {
  fetchStatus: FetchStatus;
  fileFetchStatus: FetchStatus;
  /**
   * In-flight state for `send_form`. The epic uses `exhaustMap`, so a second
   *   request while one is running is ignored rather than queued — this is what
   *   lets the UI say so instead of silently swallowing the click.
   *
   * Deliberately a single flag rather than per-case: since the epic ignores any
   *   concurrent request, every Sign button really is inert while one is in
   *   flight, and disabling all of them is the honest reflection of that.
   */
  requestFormStatus: FetchStatus;
  pendingApprovals: Record<string, (AppFileType | undefined)[]>; // key is case ID
  normalized: Record<string, FileRecord>; // key is case ID
}

const fileFetchStatus = createFetchStatusReducer({
  fetchingActions: [FETCH_APP_FILE_START],
  successActions: [FETCH_APP_FILE_SUCCESS],
  failureActions: [FETCH_APP_FILE_FAILURE],
});

const fetchStatus = createFetchStatusReducer({
  fetchingActions: [FETCH_APP_FILES_START],
  successActions: [FETCH_APP_FILES_SUCCESS],
  failureActions: [FETCH_APP_FILES_FAILURE],
});

// REQUEST_FORM_CANCEL counts as terminal, not as failure: it is what the epic
//   emits when the signer closes the embed and when a non-embedded send is only
//   confirmed by email. Either way the flow is over and Sign must come back.
const requestFormStatus = createFetchStatusReducer({
  fetchingActions: [REQUEST_FORM],
  successActions: [REQUEST_FORM_SUCCESS, REQUEST_FORM_CANCEL],
  failureActions: [REQUEST_FORM_FAILURE],
});

const pendingApprovals = handleActions<FilesState['pendingApprovals'], any>(
  {
    [UPLOAD_APP_FILE_SUCCESS]: (
      state,
      { payload }: Action<UploadAppFileSuccessPayload>,
    ) => {
      const shouldFileWaitForApproval =
        payload.uploadType === AppFileType.AbnForm ||
        payload.uploadType === AppFileType.Rx;

      if (
        !payload.caseId ||
        !payload.uploadType ||
        !shouldFileWaitForApproval
      ) {
        return state;
      }

      const caseFilesPendingApproval = state[payload.caseId] || [];
      const isUploadedFilePendingApproval = caseFilesPendingApproval.some(
        fileType => fileType === payload.uploadType,
      );

      if (isUploadedFilePendingApproval) {
        return state;
      }

      return {
        ...state,
        [payload.caseId]: [...caseFilesPendingApproval, payload.uploadType],
      };
    },
    [FETCH_APP_FILES_SUCCESS]: (
      state,
      { payload: { files, caseId } }: Action<FetchAppFilesSuccessPayload>,
    ) => {
      const approvedFiles = files.filter(file => {
        const isAbnForm = startsWith(file.fileName, AppFileType.AbnForm);
        const isRx = startsWith(file.fileName, AppFileType.Rx);
        return isAbnForm || isRx;
      });

      const previousFileTypesPendingApproval = state[caseId] || [];

      const currentFileTypesPendingApproval =
        previousFileTypesPendingApproval.filter(
          (pendingApprovalFileType?: AppFileType) =>
            approvedFiles.some(
              file => !startsWith(file.fileName, pendingApprovalFileType),
            ),
        );

      return {
        ...state,
        [caseId]: currentFileTypesPendingApproval,
      };
    },
  },
  {},
);

const normalized = handleActions<FilesState['normalized'], any>(
  {
    [UPLOAD_APP_FILE_SUCCESS]: (
      state,
      { payload }: Action<UploadAppFileSuccessPayload>,
    ) => {
      // Update the file ID
      const { filename, fileId } = payload;
      const strippedFileName = stripFileExtension({ filename: filename || '' });
      if (strippedFileName) {
        return {
          ...state,
          [payload.caseId || '']: {
            ...state[payload.caseId || ''],
            [strippedFileName]: {
              // The line above guards the case entry; this one has to as well.
              //   A file can be uploaded for a case whose file map was never
              //   loaded — or was rehydrated without it — and indexing into
              //   undefined here throws inside a reducer, which takes the store
              //   down with it.
              ...((state[payload.caseId || ''] || {})[strippedFileName] || {}),
              fileName: stripFileExtension({ filename: fileId }),
              fileId,
            },
          },
        };
      }
      return state;
    },
    [FETCH_APP_FILE_SUCCESS]: (
      state,
      {
        payload: { fileName, fileUrl, caseId },
      }: Action<FetchAppFileSuccessPayload>,
    ) => {
      const appFileType = getAppFileType(fileName) || 'unknown';
      return {
        ...state,
        [caseId]: {
          ...(state[caseId] || {}),
          [appFileType]: {
            // Same asymmetry as above: `state[caseId]` is guarded one line up
            //   and must be here too. Fetching a single file for a case whose
            //   list has not been fetched is the ordinary way to get here.
            ...((state[caseId] || {})[appFileType] || {}),
            fileUrl,
          },
        },
      };
    },
    [FETCH_APP_FILES_SUCCESS]: (
      state,
      { payload: { files, caseId } }: Action<FetchAppFilesSuccessPayload>,
    ) => ({
      /*
        NOTE: the last item for each file type in the returned attachments array
        is used. Response must be ordered or users won't see the most recent image
      */
      // overwrite previous state for security reasons
      [caseId]: keyBy(
        files,
        fileResult => getAppFileType(fileResult.fileName) || 'unknown',
      ),
    }),
  },
  {},
);

export default combineReducers({
  normalized,
  fetchStatus,
  fileFetchStatus,
  requestFormStatus,
  pendingApprovals,
});
